import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth, SignUpButton, SignInButton, UserButton } from '@clerk/clerk-react';
import {
  Globe,
  Check,
  ChevronDown,
  HelpCircle,
  Sparkles,
  Zap,
  Crown,
  Rocket,
  Clock,
  ArrowRight,
  Star,
  ShieldCheck,
} from 'lucide-react';
import { Button, Card, CardContent, LanguageModal } from '@/components/ui';
import { useBillingInfo } from '@/hooks/useSubscription';
import { useEarlyAdopterStatus, getEarlyAdopterProgress } from '@/hooks';
import { formatPrice, PRICING_TIERS, type PricingTierId } from '@/lib/stripe';
import { hasLiveStripeSubscription, tierKeyFromPlan } from '@/lib/billingAccess';
import { PlanChangeFlow, type PlanChangeRequest } from '@/components/subscription/PlanChangeFlow';
import { ReEnableUnitsOffer } from '@/components/subscription/ReEnableUnitsOffer';
import { PageSeo } from '@/components/seo/PageSeo';

interface FAQItem {
  question: { hr: string; en: string };
  answer: { hr: string; en: string };
}

const faqs: FAQItem[] = [
  {
    question: {
      hr: 'Što je Early Adopter plan?',
      en: 'What is the Early Adopter plan?',
    },
    answer: {
      hr: 'Early Adopter je ograničena ponuda za prvih 50 iznajmljivača: sve funkcionalnosti, do 5 jedinica, za 69 EUR godišnje — ta cijena ostaje dok god obnavljaš pretplatu. Kad se 50 mjesta popuni, ponuda nestaje.',
      en: 'Early Adopter is a limited offer for the first 50 hosts: every feature, up to 5 units, for 69 EUR a year — the price stays as long as you keep renewing. When the 50 places are taken, the offer is gone.',
    },
  },
  {
    question: {
      hr: 'Mogu li promijeniti plan kasnije?',
      en: 'Can I change my plan later?',
    },
    answer: {
      hr: 'Da. Veći plan (npr. Starter → Pro) i prelazak s mjesečne na godišnju naplatu vrijede odmah — plaćaš samo razliku za ostatak razdoblja, a točan iznos vidiš prije potvrde. Manji plan i prelazak s godišnje na mjesečnu naplatu vrijede od sljedeće obnove, pa ne gubiš ono što si već platio.',
      en: 'Yes. A bigger plan (e.g. Starter → Pro) and switching from monthly to yearly apply right away — you only pay the difference for the rest of the period, and you see the exact amount before you confirm. A smaller plan and switching from yearly to monthly start at your next renewal, so you don\'t lose what you already paid.',
    },
  },
  {
    question: {
      hr: 'Postoji li probni period?',
      en: 'Is there a free trial?',
    },
    answer: {
      hr: 'Nema klasičnog probnog razdoblja. Umjesto toga nudimo 30-dnevno jamstvo povrata novca na sve godišnje planove — ako u prvih 30 dana nisi zadovoljan, javi nam se i vratit ćemo cijeli iznos. Mjesečna pretplata je jeftin način da isprobaš aplikaciju.',
      en: 'No traditional free trial. Instead, every yearly plan comes with a 30-day money-back guarantee — if you\'re not satisfied in the first 30 days, we refund the full amount. The monthly plan works as a low-commitment way to try things out.',
    },
  },
  {
    question: {
      hr: 'Koja je razlika između objekta i jedinice?',
      en: 'What is the difference between a property and a unit?',
    },
    answer: {
      hr: 'Objekt je zgrada ili lokacija (npr. vila, kuća). Jedinica je prostor za iznajmljivanje unutar objekta (npr. apartman, soba). Plan određuje koliko aktivnih jedinica možeš imati.',
      en: 'A property is a building or location (e.g., villa, house). A unit is a rental space within a property (e.g., apartment, room). Your plan sets how many active units you can have.',
    },
  },
  {
    question: {
      hr: 'Mogu li otkazati pretplatu?',
      en: 'Can I cancel my subscription?',
    },
    answer: {
      hr: 'Da, automatsku obnovu možeš isključiti bilo kada. Sve radi do kraja plaćenog razdoblja; nakon toga još 30 dana gosti vide svoje portale, a ti možeš platiti i nastaviti. Ako ne platiš, račun se suspendira, ali tvoji podaci ostaju sačuvani. Nema skrivenih naknada ni penala.',
      en: 'Yes, you can turn off automatic renewal at any time. Everything works until the end of the paid period; after that, guests keep seeing their portals for another 30 days and you can still pay to continue. If you don\'t, the account is suspended, but your data is kept. No hidden fees or penalties.',
    },
  },
];

function FAQAccordion({ item, isHr }: { item: FAQItem; isHr: boolean }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <CardContent className="p-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full p-5 text-left hover:bg-[hsl(var(--muted))]/30 transition-colors"
      >
        <span className="font-semibold pr-4">{isHr ? item.question.hr : item.question.en}</span>
        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-indigo-500 text-white rotate-180' : 'bg-[hsl(var(--muted))]'}`}>
          <ChevronDown className="h-5 w-5" />
        </div>
      </button>
      <div className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
        <div className="overflow-hidden">
          <p className="px-5 pb-5 text-[hsl(var(--muted-foreground))] leading-relaxed">
            {isHr ? item.answer.hr : item.answer.en}
          </p>
        </div>
      </div>
    </CardContent>
  );
}

// Early Adopter Progress Bar Component
function EarlyAdopterProgressBar({ isHr }: { isHr: boolean }) {
  const { data: earlyAdopterStatus } = useEarlyAdopterStatus();
  const progress = getEarlyAdopterProgress(earlyAdopterStatus);

  if (!progress.known) return null;
  if (progress.isSoldOut) {
    return (
      <div className="text-center text-sm text-[hsl(var(--muted-foreground))]">
        <span className="font-semibold text-red-500">
          {isHr ? 'Rasprodano!' : 'Sold out!'}
        </span>
      </div>
    );
  }

  const urgencyColors: Record<string, string> = {
    low: 'bg-green-500',
    medium: 'bg-yellow-500',
    high: 'bg-red-500',
    sold_out: 'bg-gray-500',
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-[hsl(var(--muted-foreground))]">
          {isHr ? 'Preostalo:' : 'Remaining:'}
        </span>
        <span className={`font-bold ${progress.isAlmostGone ? 'text-red-500 animate-pulse' : ''}`}>
          {progress.remaining}/{progress.total}
        </span>
      </div>
      <div className="h-2 bg-[hsl(var(--muted))] rounded-full overflow-hidden">
        <div
          className={`h-full ${urgencyColors[progress.urgencyLevel] || 'bg-gray-500'} transition-all duration-500`}
          style={{ width: `${progress.percentage}%` }}
        />
      </div>
      {progress.isAlmostGone && (
        <p className="text-xs text-red-500 font-medium flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {isHr ? 'Još samo ' + progress.remaining + ' mjesta!' : 'Only ' + progress.remaining + ' spots left!'}
        </p>
      )}
    </div>
  );
}

// Pricing Tier Card
interface TierCardProps {
  tier: {
    id: string;
    name: string;
    yearlyPrice: number;
    monthlyPrice?: number;
    maxUnits: number;
    features?: readonly string[];
    isLimited?: boolean;
    totalSlots?: number;
  };
  isHr: boolean;
  isPopular?: boolean;
  isEarlyAdopter?: boolean;
  /**
   * Selected billing interval. Drives which price + suffix the card shows
   * AND which interval is sent to Stripe checkout.
   * Early Adopter is yearly-only (limited promo); for that tier we ignore
   * `interval` and always render yearly.
   */
  interval: 'month' | 'year';
  onSelect: () => void;
  isLoading?: boolean;
  isSignedIn: boolean;
  /** The host's current plan on this interval → button disabled. */
  isCurrent?: boolean;
  /** The current plan while a change is scheduled → "keep this plan" (cancels the change). */
  canKeep?: boolean;
}

function TierCard({ tier, isHr, isPopular, isEarlyAdopter, interval, onSelect, isLoading, isSignedIn, isCurrent = false, canKeep = false }: TierCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const tierIcons = {
    early_adopter: Sparkles,
    starter: Zap,
    pro: Rocket,
    business: Crown,
  };

  const Icon = tierIcons[tier.id as keyof typeof tierIcons] || Zap;

  const tierDescriptions = {
    early_adopter: {
      hr: 'Za prve korisnike — najbolja cijena dok god obnavljaš',
      en: 'For the first hosts — the best price for as long as you renew',
    },
    starter: {
      hr: 'Idealno za male iznajmljivače s 1 do 3 apartmana',
      en: 'Perfect for small hosts with 1 to 3 apartments',
    },
    pro: {
      hr: 'Za iznajmljivače s više jedinica',
      en: 'For serious hosts with multiple units',
    },
    business: {
      hr: 'Za agencije i veće iznajmljivače',
      en: 'For agencies and larger hosts',
    },
  };

  const description = tierDescriptions[tier.id as keyof typeof tierDescriptions];

  // Color schemes for each tier
  const tierColors = {
    early_adopter: {
      gradient: 'from-amber-500 via-orange-500 to-red-500',
      glow: 'shadow-amber-500/25',
      icon: 'text-amber-500',
      iconBg: 'bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/40 dark:to-orange-900/40',
      border: 'border-amber-200 dark:border-amber-800',
      badge: 'bg-gradient-to-r from-amber-500 to-orange-500',
    },
    starter: {
      gradient: 'from-blue-500 to-cyan-500',
      glow: 'shadow-blue-500/20',
      icon: 'text-blue-500',
      iconBg: 'bg-blue-50 dark:bg-blue-900/30',
      border: 'border-gray-200 dark:border-gray-700',
      badge: 'bg-blue-500',
    },
    pro: {
      gradient: 'from-cyan-500 via-blue-500 to-blue-600',
      glow: 'shadow-indigo-500/30',
      icon: 'text-indigo-500',
      iconBg: 'bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30',
      border: 'border-indigo-200 dark:border-indigo-800',
      badge: 'bg-gradient-to-r from-blue-600 to-indigo-600',
    },
    business: {
      gradient: 'from-blue-600 via-cyan-600 to-teal-600',
      glow: 'shadow-blue-500/20',
      icon: 'text-blue-500',
      iconBg: 'bg-blue-50 dark:bg-blue-900/30',
      border: 'border-gray-200 dark:border-gray-700',
      badge: 'bg-blue-500',
    },
  };

  const colors = tierColors[tier.id as keyof typeof tierColors] || tierColors.starter;

  return (
    <div
      className={`group relative`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Animated gradient border for Early Adopter and Popular */}
      {(isEarlyAdopter || isPopular) && (
        <div className={`absolute -inset-[2px] bg-gradient-to-r ${colors.gradient} rounded-2xl opacity-75 blur-sm group-hover:opacity-100 transition-opacity duration-500 ${isEarlyAdopter ? 'animate-pulse' : ''}`} />
      )}

      <Card className={`relative overflow-hidden transition-all duration-500 rounded-2xl border-2 ${colors.border}
        ${isPopular ? 'scale-105 z-10' : 'hover:scale-[1.02]'}
        ${isEarlyAdopter ? 'bg-gradient-to-br from-amber-50/90 via-orange-50/90 to-yellow-50/90 dark:from-amber-950/40 dark:via-orange-950/40 dark:to-yellow-950/40' : 'bg-[hsl(var(--card))]/95 backdrop-blur-sm'}
        ${isHovered ? `shadow-2xl ${colors.glow}` : 'shadow-lg'}
      `}>

        {/* Decorative elements */}
        <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${colors.gradient} opacity-5 rounded-full -translate-y-16 translate-x-16`} />
        <div className={`absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr ${colors.gradient} opacity-5 rounded-full translate-y-12 -translate-x-12`} />

        {/* Popular Badge */}
        {isPopular && (
          <div className={`absolute top-4 right-4 ${colors.badge} text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1`}>
            <Star className="h-3 w-3 fill-current" />
            {isHr ? 'Najpopularniji' : 'Most Popular'}
          </div>
        )}

        {/* Early Adopter Badge */}
        {isEarlyAdopter && (
          <div className={`absolute top-0 left-0 right-0 ${colors.badge} text-white text-xs font-bold px-3 py-2 text-center shadow-lg`}>
            <span className="flex items-center justify-center gap-2">
              <Sparkles className="h-4 w-4 animate-pulse" />
              {isHr ? 'LIMITIRANA PONUDA' : 'LIMITED OFFER'}
              <Sparkles className="h-4 w-4 animate-pulse" />
            </span>
          </div>
        )}

        <CardContent className={`relative p-6 ${isEarlyAdopter ? 'pt-12' : 'pt-6'}`}>
          {/* Header */}
          <div className="flex items-start gap-4 mb-5">
            <div className={`p-3 rounded-xl ${colors.iconBg} shadow-sm transition-transform duration-300 ${isHovered ? 'scale-110 rotate-3' : ''}`}>
              <Icon className={`h-7 w-7 ${colors.icon}`} />
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-bold tracking-tight">{tier.name}</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))] font-medium">
                {tier.id === 'starter'
                  ? (isHr ? '1-3 jedinice' : '1-3 units')
                  : tier.id === 'pro'
                    ? (isHr ? '4-6 jedinica' : '4-6 units')
                    : tier.id === 'business'
                      ? (isHr ? '7-10 jedinica' : '7-10 units')
                      : (isHr ? `Do ${tier.maxUnits} jedinica` : `Up to ${tier.maxUnits} units`)
                }
              </p>
            </div>
          </div>

          {/* Description */}
          {description && (
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-5 leading-relaxed">
              {isHr ? description.hr : description.en}
            </p>
          )}

          {/* Price — switches with interval toggle. Early Adopter is yearly-only. */}
          <div className="mb-6 pb-6 border-b border-[hsl(var(--border))]" data-testid={`tier-price-${tier.id}`}>
            {(() => {
              // Early Adopter is locked to yearly even when toggle says monthly.
              const showMonthly = interval === 'month' && !isEarlyAdopter && tier.monthlyPrice != null;
              const displayPrice = showMonthly ? tier.monthlyPrice! : tier.yearlyPrice;
              const suffix = showMonthly ? (isHr ? 'mj' : 'mo') : (isHr ? 'god' : 'yr');
              return (
                <>
                  <div className="flex items-baseline gap-1">
                    <span
                      className={`text-5xl font-black tracking-tight ${isEarlyAdopter ? 'bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent' : ''}`}
                      data-testid={`tier-amount-${tier.id}`}
                    >
                      {formatPrice(displayPrice)}
                    </span>
                    <span className="text-lg text-[hsl(var(--muted-foreground))] font-medium">/{suffix}</span>
                  </div>
                  {showMonthly ? (
                    // Tease the yearly savings to nudge upgrades.
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1 flex items-center gap-1">
                      <span className="inline-block w-1 h-1 rounded-full bg-green-500" />
                      {isHr ? 'ili' : 'or'} {formatPrice(tier.yearlyPrice)}/{isHr ? 'god' : 'yr'} (
                      {isHr ? 'ušteda' : 'save'} ~{Math.round((1 - tier.yearlyPrice / (tier.monthlyPrice! * 12)) * 100)}%)
                    </p>
                  ) : (
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1 flex items-center gap-1">
                      <span className="inline-block w-1 h-1 rounded-full bg-green-500" />
                      ~{formatPrice(Math.round(tier.yearlyPrice / 12))}/{isHr ? 'mj' : 'mo'}
                    </p>
                  )}
                </>
              );
            })()}
          </div>

          {/* Early Adopter Progress */}
          {isEarlyAdopter && (
            <div className="mb-6">
              <EarlyAdopterProgressBar isHr={isHr} />
            </div>
          )}

          {/* Features - Enhanced */}
          <ul className="space-y-3 mb-6">
            {[
              { hr: 'Sve funkcionalnosti', en: 'All features' },
              { hr: 'Neograničene rezervacije', en: 'Unlimited bookings' },
              { hr: 'Real-time chat s prijevodom', en: 'Real-time chat with translation' },
              { hr: 'AI asistent za goste', en: 'AI guest assistant' },
            ].map((feature, index) => (
              <li key={index} className="flex items-center gap-3 text-sm">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Check className="h-3 w-3 text-green-600 dark:text-green-400" />
                </div>
                <span>{isHr ? feature.hr : feature.en}</span>
              </li>
            ))}
            {(tier.id === 'pro' || tier.id === 'business' || tier.id === 'early_adopter') && (
              <li className="flex items-center gap-3 text-sm">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Check className="h-3 w-3 text-green-600 dark:text-green-400" />
                </div>
                <span>{isHr ? 'Prioritetna podrška' : 'Priority support'}</span>
              </li>
            )}
            {tier.id === 'early_adopter' && (
              <li className="flex items-center gap-3 text-sm font-semibold text-amber-600 dark:text-amber-400">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Sparkles className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                </div>
                <span>{isHr ? 'Early Adopter oznaka' : 'Early Adopter badge'}</span>
              </li>
            )}
          </ul>

          {/* CTA Button - Enhanced */}
          {isSignedIn ? (
            <Button
              className={`w-full h-12 text-base font-semibold rounded-xl transition-all duration-300 ${
                isEarlyAdopter
                  ? 'bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 hover:from-amber-600 hover:via-orange-600 hover:to-red-600 shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/30'
                  : isPopular
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30'
                    : 'hover:bg-[hsl(var(--accent))]'
              }`}
              variant={isPopular || isEarlyAdopter ? 'default' : 'outline'}
              onClick={onSelect}
              disabled={isLoading || (isCurrent && !canKeep)}
            >
              {isCurrent && canKeep ? (
                <span>{isHr ? 'Zadrži ovaj plan' : 'Keep this plan'}</span>
              ) : isCurrent ? (
                <span>{isHr ? 'Tvoj trenutni plan' : 'Your current plan'}</span>
              ) : isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>{isHr ? 'Učitavanje...' : 'Loading...'}</span>
                </div>
              ) : (
                <span className="flex items-center gap-2">
                  {isEarlyAdopter ? (
                    isHr ? 'Postani Early Adopter' : 'Become an Early Adopter'
                  ) : (
                    isHr ? 'Odaberi plan' : 'Select Plan'
                  )}
                  <ArrowRight className={`h-4 w-4 transition-transform duration-300 ${isHovered ? 'translate-x-1' : ''}`} />
                </span>
              )}
            </Button>
          ) : (
            <SignUpButton
              mode="modal"
              forceRedirectUrl={`/pricing?plan=${tier.id === 'early_adopter' ? 'earlyAdopter' : tier.id}`}
            >
              <Button
                className={`w-full h-12 text-base font-semibold rounded-xl transition-all duration-300 ${
                  isEarlyAdopter
                    ? 'bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 hover:from-amber-600 hover:via-orange-600 hover:to-red-600 shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/30'
                    : isPopular
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30'
                      : 'hover:bg-[hsl(var(--accent))]'
                }`}
                variant={isPopular || isEarlyAdopter ? 'default' : 'outline'}
              >
                <span className="flex items-center gap-2">
                  {isHr ? 'Registriraj se' : 'Sign Up'}
                  <ArrowRight className="h-4 w-4" />
                </span>
              </Button>
            </SignUpButton>
          )}

        </CardContent>
      </Card>
    </div>
  );
}

export function PricingPage() {
  const { i18n } = useTranslation();
  const isHr = i18n.language === 'hr';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isSignedIn } = useAuth();

  const billingQuery = useBillingInfo();
  const billing = billingQuery.data;
  // The billing query runs only once auth is ready (useAuthReady). A row of
  // null means the profile is still being created (it keeps polling) — wait
  // for a real row, an error, or ~30 s (the hook's own polling window).
  const [nullWaitOver, setNullWaitOver] = useState(false);
  const rowMissing = billingQuery.isSuccess && billingQuery.data === null;
  useEffect(() => {
    if (!rowMissing) return;
    const id = setTimeout(() => setNullWaitOver(true), 30_000);
    return () => clearTimeout(id);
  }, [rowMissing]);
  const billingSettled =
    (!!billingQuery.isSuccess && billingQuery.data != null) || !!billingQuery.isError || (rowMissing && nullWaitOver);
  const { data: earlyAdopterStatus } = useEarlyAdopterStatus();
  const earlyAdopterProgress = getEarlyAdopterProgress(earlyAdopterStatus);
  // Early Adopter only while real slots are left, or for a host who holds one.
  const showEarlyAdopter = earlyAdopterProgress.isAvailable || billing?.ea_slot != null;
  const [changeRequest, setChangeRequest] = useState<PlanChangeRequest | null>(null);

  // Current plan (only a live Stripe subscription is "current" — a gifted
  // host may buy the same tier to continue after the gift).
  const live = hasLiveStripeSubscription(billing);
  const currentTier = live ? tierKeyFromPlan(billing?.subscription_plan) : null;
  const currentInterval = live ? (billing?.billing_interval === 'month' ? 'month' : 'year') : null;
  const isCurrent = (tierKey: PricingTierId, iv: 'month' | 'year') => currentTier === tierKey && currentInterval === iv;
  // A scheduled change exists → picking the current plan means "keep it".
  const hasPendingChange = live && !!billing?.pending_plan;

  // Billing-interval toggle. Default to yearly: it's the better deal (~17%
  // off) and aligns with how the seasonal-rental business cashflows.
  // Early Adopter ignores this — it's a yearly-only promo by design.
  const [interval, setInterval] = useState<'month' | 'year'>('year');

  // Back from a cancelled Stripe Checkout. Read after mount: the page is
  // prerendered (SSG) and the first client render must match the HTML.
  const [showCancelled, setShowCancelled] = useState(false);
  // Came back after sign-up with a plan selected
  const selectedPlan = searchParams.get('plan');

  // Every purchase / change opens the confirmation (exact amount + date)
  // before anything is charged.
  const handleSelectPlan = (tierId: string) => {
    if (!isSignedIn) return;
    if (!(tierId in PRICING_TIERS)) return;
    const tier = tierId as PricingTierId;
    // Early Adopter is yearly-only by design (limited promo).
    const checkoutInterval = tier === 'earlyAdopter' ? 'year' : interval;
    // The host already has this plan: nothing to buy (unless a change is
    // scheduled — then this cancels it, confirmed in the dialog).
    if (isCurrent(tier, checkoutInterval) && !hasPendingChange) return;
    setChangeRequest({ tier, interval: checkoutInterval });
  };

  useEffect(() => {
    if (searchParams.get('cancelled') === 'true') {
      setShowCancelled(true);
      navigate('/pricing', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Just signed up with a plan selected → open its confirmation — but only
  // once auth is ready and the billing row is known (current plan / pending
  // change decide what the click means).
  useEffect(() => {
    if (isSignedIn && selectedPlan && billingSettled) {
      const validPlans = ['earlyAdopter', 'starter', 'pro', 'business'];
      if (validPlans.includes(selectedPlan)) {
        navigate('/pricing', { replace: true });
        handleSelectPlan(selectedPlan);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, selectedPlan, billingSettled]);

  const [langOpen, setLangOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[hsl(var(--background))] via-[hsl(var(--background))] to-blue-50/30 dark:to-blue-950/10">
      <PageSeo
        path="/pricing"
        title={isHr ? 'Cijene i paketi | BookiApp' : 'Pricing & Plans | BookiApp'}
        description={isHr
          ? 'Godišnja ili mjesečna pretplata za iznajmljivače s 1 do 10 smještajnih jedinica. Sve funkcionalnosti u svakom paketu, bez skrivenih troškova.'
          : 'Yearly or monthly plans for hosts with 1 to 10 rental units. Every feature in every plan, no hidden fees.'}
      />
      {/* Decorative background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute top-40 right-20 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-40 left-1/3 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative container mx-auto flex items-center justify-between p-4">
        <Link to="/" className="flex items-center gap-2 group">
          <img src="/favicon-original.png" alt="BookiApp" className="h-8 w-auto group-hover:scale-105 transition-transform" />
        </Link>
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => setLangOpen(true)} className="gap-2" aria-haspopup="dialog" aria-expanded={langOpen}>
            <Globe className="h-4 w-4" />
            {i18n.language.toUpperCase()}
          </Button>
          {isSignedIn ? (
            <>
              <Link to="/dashboard">
                <Button className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 shadow-lg shadow-blue-500/25">
                  Dashboard
                </Button>
              </Link>
              <UserButton
                afterSignOutUrl="/"
                appearance={{ elements: { avatarBox: 'h-9 w-9 rounded-xl ring-2 ring-transparent hover:ring-blue-200 transition-all duration-200' } }}
              />
            </>
          ) : (
            <SignInButton mode="modal" forceRedirectUrl="/dashboard" signUpForceRedirectUrl="/dashboard">
              <Button variant="ghost">{isHr ? 'Prijava' : 'Sign In'}</Button>
            </SignInButton>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="relative container mx-auto px-4 py-16 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-sm font-medium mb-6">
          <Sparkles className="h-4 w-4" />
          {isHr ? 'Jednostavno i transparentno' : 'Simple and transparent'}
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6">
          <span className="bg-gradient-to-r from-cyan-500 via-blue-500 to-blue-600 bg-clip-text text-transparent">
            {isHr ? 'Odaberi svoj plan' : 'Choose Your Plan'}
          </span>
        </h1>
        <p className="text-lg sm:text-xl text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto mb-3 leading-relaxed">
          {isHr
            ? 'Jedna cijena za sve funkcionalnosti. Bez skrivenih troškova.'
            : 'One price for all features. No hidden fees.'}
        </p>
        <p className="text-sm text-[hsl(var(--muted-foreground))] italic">
          {isHr
            ? '"Ako ti BookiApp uštedi samo nekoliko sati godišnje, već si u plusu."'
            : '"If BookiApp saves you just a few hours a year, you\'re already ahead."'}
        </p>
      </section>

      {/* Pricing Tiers */}
      <section className="container mx-auto px-4 py-12">
        {showCancelled && (
          <div className="max-w-2xl mx-auto mb-6 p-3 rounded-lg bg-[hsl(var(--muted))] text-sm space-y-2">
            <div className="flex items-center gap-3">
              <span className="flex-1">
                {isHr ? 'Plaćanje je prekinuto — ništa nije naplaćeno.' : 'Payment was cancelled — nothing was charged.'}
              </span>
              <Button variant="ghost" size="sm" onClick={() => setShowCancelled(false)}>
                {isHr ? 'Zatvori' : 'Close'}
              </Button>
            </div>
            <ReEnableUnitsOffer billing={billing} />
          </div>
        )}
        {/* Monthly / Yearly toggle */}
        <div className="flex justify-center mb-10" role="radiogroup" aria-label={isHr ? 'Razdoblje naplate' : 'Billing interval'}>
          <div className="inline-flex items-center bg-[hsl(var(--muted))] rounded-full p-1 shadow-sm border border-[hsl(var(--border))]">
            <button
              type="button"
              role="radio"
              aria-checked={interval === 'month'}
              data-testid="interval-toggle-month"
              onClick={() => setInterval('month')}
              className={`px-5 py-2 text-sm font-semibold rounded-full transition-all ${
                interval === 'month'
                  ? 'bg-[hsl(var(--background))] text-[hsl(var(--foreground))] shadow-md'
                  : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
              }`}
            >
              {isHr ? 'Mjesečno' : 'Monthly'}
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={interval === 'year'}
              data-testid="interval-toggle-year"
              onClick={() => setInterval('year')}
              className={`px-5 py-2 text-sm font-semibold rounded-full transition-all flex items-center gap-2 ${
                interval === 'year'
                  ? 'bg-[hsl(var(--background))] text-[hsl(var(--foreground))] shadow-md'
                  : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
              }`}
            >
              {isHr ? 'Godišnje' : 'Yearly'}
              <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-green-500 text-white">
                -17%
              </span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {/* Early Adopter Tier — always yearly (limited promo) */}
          {showEarlyAdopter && (
            <TierCard
              tier={PRICING_TIERS.earlyAdopter}
              isHr={isHr}
              isEarlyAdopter={true}
              interval="year"
              onSelect={() => handleSelectPlan('earlyAdopter')}
              isSignedIn={isSignedIn ?? false}
              isCurrent={isCurrent('earlyAdopter', 'year')}
              canKeep={hasPendingChange}
            />
          )}

          {/* Starter Tier */}
          <TierCard
            tier={PRICING_TIERS.starter}
            isHr={isHr}
            interval={interval}
            onSelect={() => handleSelectPlan('starter')}
            isSignedIn={isSignedIn ?? false}
            isCurrent={isCurrent('starter', interval)}
            canKeep={hasPendingChange}
          />

          {/* Pro Tier */}
          <TierCard
            tier={PRICING_TIERS.pro}
            isHr={isHr}
            isPopular={true}
            interval={interval}
            onSelect={() => handleSelectPlan('pro')}
            isSignedIn={isSignedIn ?? false}
            isCurrent={isCurrent('pro', interval)}
            canKeep={hasPendingChange}
          />

          {/* Business Tier */}
          <TierCard
            tier={PRICING_TIERS.business}
            isHr={isHr}
            interval={interval}
            onSelect={() => handleSelectPlan('business')}
            isSignedIn={isSignedIn ?? false}
            isCurrent={isCurrent('business', interval)}
            canKeep={hasPendingChange}
          />
        </div>

        {/* 30-day money-back guarantee ribbon */}
        <div className="max-w-2xl mx-auto mt-8 px-4">
          <div className="flex items-center justify-center gap-3 p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
            <ShieldCheck className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
            <p className="text-sm text-green-800 dark:text-green-200 text-center">
              <strong>{isHr ? '30-dnevno jamstvo povrata novca' : '30-day money-back guarantee'}</strong>
              {' — '}
              {isHr
                ? 'na sve godišnje planove. Ako nisi zadovoljan u prvih 30 dana, javi nam se i vratit ćemo cijeli iznos.'
                : 'on all yearly plans. If you\'re not satisfied within the first 30 days, we refund in full.'}
            </p>
          </div>
        </div>

        {/* Enterprise CTA */}
        <div className="text-center mt-8">
          <p className="text-[hsl(var(--muted-foreground))]">
            {isHr ? 'Više od 10 jedinica?' : 'More than 10 units?'}
          </p>
          <Link to="/contact">
            <Button variant="link" className="text-[hsl(var(--primary))]">
              {isHr ? 'Javi nam se za ponudu' : 'Contact us for a quote'}
            </Button>
          </Link>
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="container mx-auto px-4 py-16 md:py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 text-green-600 dark:text-green-400 text-sm font-medium mb-4">
            <Check className="h-4 w-4" />
            {isHr ? 'Sve uključeno' : 'Everything included'}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
              {isHr ? 'Sve funkcionalnosti uključene' : 'All Features Included'}
            </span>
          </h2>
          <p className="text-[hsl(var(--muted-foreground))] max-w-xl mx-auto">
            {isHr
              ? 'Bez dodatnih naknada. Bez skrivenih troškova. Sve što trebate za profesionalno iznajmljivanje.'
              : 'No extra fees. No hidden costs. Everything you need for professional hosting.'}
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <Card className="overflow-hidden border-2 border-green-500/20 shadow-xl shadow-green-500/5">
            <div className="h-1 bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500" />
            <CardContent className="p-6 md:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {[
                  { hr: 'Personalizirana aplikacija za goste', en: 'Personalized Guest App', icon: '📱' },
                  { hr: 'Real-time chat s prijevodom', en: 'Real-time translated chat', icon: '💬' },
                  { hr: 'AI asistent za goste', en: 'AI guest assistant', icon: '🤖' },
                  { hr: 'Push notifikacije', en: 'Push notifications', icon: '🔔' },
                  { hr: 'iCal sync (Booking, Airbnb)', en: 'iCal sync (Booking, Airbnb)', icon: '📅' },
                  { hr: 'Sezonske cijene', en: 'Seasonal pricing', icon: '💰' },
                  { hr: 'Grupne rezervacije', en: 'Group bookings', icon: '👨‍👩‍👧‍👦' },
                  { hr: 'Lokalne preporuke', en: 'Local recommendations', icon: '📍' },
                  { hr: 'Vremenska prognoza', en: 'Weather forecast', icon: '🌤️' },
                  { hr: 'QR kodovi za pristup', en: 'QR access codes', icon: '🔗' },
                  { hr: 'Neograničene rezervacije', en: 'Unlimited bookings', icon: '♾️' },
                  { hr: 'Upload slika', en: 'Image upload', icon: '🖼️' },
                ].map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 rounded-xl bg-[hsl(var(--muted))]/30 hover:bg-green-500/10 transition-colors group"
                  >
                    <span className="text-xl group-hover:scale-110 transition-transform">{feature.icon}</span>
                    <span className="text-sm font-medium">{isHr ? feature.hr : feature.en}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ */}
      <section className="container mx-auto px-4 py-16 md:py-20">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 text-sm font-medium mb-4">
              <HelpCircle className="h-4 w-4" />
              {isHr ? 'Imate pitanja?' : 'Have questions?'}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold">
              <span className="bg-gradient-to-r from-cyan-500 to-blue-600 bg-clip-text text-transparent">
                {isHr ? 'Česta pitanja' : 'Frequently Asked Questions'}
              </span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card
                key={index}
                className="overflow-hidden border-2 border-[hsl(var(--border))] hover:border-indigo-500/30 transition-all duration-300 hover:shadow-lg hover:shadow-indigo-500/5"
              >
                <FAQAccordion item={faq} isHr={isHr} />
              </Card>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">
              {isHr ? 'Niste pronašli odgovor?' : "Didn't find your answer?"}
            </p>
            <Link to="/contact">
              <Button variant="outline" className="gap-2 group">
                {isHr ? 'Kontaktirajte nas' : 'Contact us'}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 py-16 md:py-20">
        <div className="max-w-4xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden">
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-blue-500 to-blue-600" />
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIgMS44LTQgNC00czQgMS44IDQgNC0xLjggNC00IDQtNC0xLjgtNC00eiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />

            <div className="relative p-8 md:p-12 lg:p-16 text-center text-white">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-sm font-medium mb-6">
                <Sparkles className="h-4 w-4" />
                {isHr ? 'Počnite već danas' : 'Start today'}
              </div>

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
                {isHr ? 'Spremni za promjenu?' : 'Ready for a change?'}
              </h2>
              <p className="text-lg md:text-xl opacity-90 mb-8 max-w-xl mx-auto">
                {isHr
                  ? 'Pridružite se iznajmljivačima koji pružaju premium iskustvo gostima.'
                  : 'Join hosts who provide premium guest experiences.'}
              </p>

              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button
                  size="lg"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="bg-white text-indigo-600 hover:bg-white/90 shadow-xl shadow-black/20 text-base md:text-lg px-8 group"
                >
                  {isHr ? 'Pogledaj planove' : 'View plans'}
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Link to="/contact">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-white/30 bg-transparent text-white hover:bg-white/10 text-base md:text-lg px-8"
                  >
                    {isHr ? 'Kontaktirajte nas' : 'Contact us'}
                  </Button>
                </Link>
              </div>

              {/* Trust badges */}
              <div className="mt-10 pt-8 border-t border-white/20 flex flex-wrap justify-center gap-6 text-sm opacity-80">
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  {isHr ? 'Bez ugovora' : 'No contracts'}
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  {isHr ? 'Otkažite bilo kada' : 'Cancel anytime'}
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  {isHr ? 'Sigurno plaćanje' : 'Secure payment'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-8">
        <div className="container mx-auto px-4 text-center text-sm text-[hsl(var(--muted-foreground))]">
          <p>&copy; 2026 BookiApp. {isHr ? 'Sva prava pridržana.' : 'All rights reserved.'}</p>
        </div>
      </footer>

      <LanguageModal open={langOpen} onClose={() => setLangOpen(false)} scope="marketing" />

      <PlanChangeFlow
        request={changeRequest}
        billing={billing}
        returnTo="pricing"
        onClose={() => setChangeRequest(null)}
      />
    </div>
  );
}

export default PricingPage;
